<template>
  <div id="fusion_contacts_list_container" class="w-full h-full flex flex-col">
    <!-- Search active: alternate body states (initial / results / no-results) -->
    <template v-if="messenger_search_active">
      <div class="flex-1 overflow-y-auto pl-[18px] pr-2 pb-3 min-h-0">
        <p v-if="enterprise_users_processing" class="text-center text-text-default/70 my-5">{{ $t('messaging.contacts.loading') }}</p>

        <!-- Initial empty state: search is open but no query yet -->
        <div v-else-if="!messenger_search_query_trimmed" class="w-full h-full flex flex-col items-center justify-center px-6 text-center">
          <p class="text-text-placeholder text-base font-semibold leading-snug mb-2">{{ $t('messaging.contacts.sendMessageTitle') }}</p>
          <p class="text-text-placeholder text-base leading-snug max-w-xs">{{ $t('messaging.contacts.searchHint') }}</p>
        </div>

        <!-- Has matches: results counter divider + flat list (no section dividers, no rail) -->
        <template v-else-if="$FUSarrayHasEntries(contacts_filtered)">
          <div class="flex items-center pt-4 w-full">
            <div class="flex flex-1 flex-col items-start pb-1 border-b border-border-separator">
              <p class="text-sm text-text-subtle leading-5 tracking-[0.16px]">{{ search_results_label }}</p>
            </div>
          </div>
          <div v-for="(user, idx) in contacts_filtered" :key="`fusion_contact_row_${user.personGuid}`"
               class="flex gap-2 items-center w-full bg-surface-1 hover:bg-black/5 cursor-pointer"
               @click.prevent="openContact(user.personGuid)">
            <messenger-avatar :name="user.fullName" :size="32" :bg-color="color_for_guid[user.personGuid]" />
            <div class="flex-1 flex gap-3 items-center min-w-0 py-4"
                 :class="{'border-b border-border-separator': idx < contacts_filtered.length - 1}">
              <span class="text-text-default/80 text-sm font-medium truncate">{{ user.fullName }}</span>
              <span class="text-text-default/70 text-sm truncate">{{ user.email }}</span>
            </div>
          </div>
        </template>

        <!-- No matches: illustration + helper text -->
        <div v-else class="w-full h-full flex flex-col items-center justify-center px-6 text-center">
          <img :src="contacts_search_empty_icon" alt="" class="w-24 h-24 mb-6 opacity-90" />
          <p class="text-text-placeholder text-base font-semibold leading-snug mb-2">{{ $t('messaging.search.noResultsFor', { query: messenger_search_query_trimmed }) }}</p>
          <p class="text-text-placeholder text-base leading-snug max-w-xs">{{ $t('messaging.search.noResultsHint') }}</p>
        </div>
      </div>
    </template>

    <!-- Default: grouped list + right rail -->
    <template v-else>
      <div class="flex-1 flex gap-4 pl-[18px] pr-2 pb-3 min-h-0">
        <div ref="scroll_container" class="flex-1 overflow-y-auto min-w-0">
          <p v-if="enterprise_users_processing" class="text-center text-text-default/70 my-5">{{ $t('messaging.contacts.loading') }}</p>
          <template v-else-if="$FUSarrayHasEntries(contacts_grouped)">
            <div v-for="group in contacts_grouped" :key="`fusion_contact_group_${group.letter}`">
              <div :id="`fusion_contacts_section_${group.letter}`" class="flex items-center pt-4 w-full">
                <div class="flex flex-1 flex-col items-start pb-1 border-b border-border-separator">
                  <p class="text-sm text-text-subtle leading-5 tracking-[0.16px]">{{ group.letter }}</p>
                </div>
              </div>
              <div v-for="(user, idx) in group.contacts" :key="`fusion_contact_row_${user.personGuid}`"
                   class="flex gap-2 items-center w-full bg-surface-1 hover:bg-black/5 cursor-pointer"
               @click.prevent="openContact(user.personGuid)">
                <messenger-avatar :name="user.fullName" :size="32" :bg-color="color_for_guid[user.personGuid]" />
                <div class="flex-1 flex gap-3 items-center min-w-0 py-4"
                     :class="{'border-b border-border-separator': idx < group.contacts.length - 1}">
                  <span class="text-text-default/80 text-sm font-medium truncate">{{ user.fullName }}</span>
                  <span class="text-text-default/70 text-sm truncate">{{ user.email }}</span>
                </div>
              </div>
            </div>
          </template>
          <p v-else class="text-center text-text-default/70 my-5">{{ $t('messaging.contacts.none') }}</p>
        </div>

        <div v-if="$FUSarrayHasEntries(contacts_sorted)" class="w-[13px] flex flex-col items-center justify-center uppercase text-sm text-text-placeholder select-none flex-shrink-0">
          <p v-for="letter in alphabet_letters" :key="`fusion_contacts_jump_${letter}`"
             class="-mb-0.5 w-full text-center leading-tight tracking-[0.16px] cursor-pointer hover:text-text-default"
             @click="jumpToLetter(letter)">
            {{ letter }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
  #fusion_contacts_list_container input:focus {
    box-shadow: none;
  }
</style>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
import ContactsSearchEmpty from '@images/contacts_search_empty.svg';

export default {
  name: 'ContactsList',
  components: {
    'messenger-avatar': MessengerAvatar,
  },
  beforeUnmount() {
    // Catch any path that removes the Contacts subview without going through the navbar's
    // setMessagingState (e.g. FusionBody.setActiveTab resetting internal_messaging_state).
    this.resetMessengerSearch();
  },
  data() {
    return {
      contacts_search_empty_icon: ContactsSearchEmpty,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    enterprise_users_data() {
      return this.mainStore.enterprise_users_data;
    },
    enterprise_users_processing() {
      return this.mainStore.enterprise_users_processing;
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    messenger_search_active: {
      get() { return this.mainStore.messenger_search_active; },
      set(value) { this.mainStore.messenger_search_active = value; },
    },
    messenger_search_query: {
      get() { return this.mainStore.messenger_search_query; },
      set(value) { this.mainStore.messenger_search_query = value; },
    },
    open_contact_guid: {
      get() { return this.mainStore.open_contact_guid; },
      set(value) { this.mainStore.open_contact_guid = value; },
    },
    pending_new_message_recipient_guid: {
      get() { return this.mainStore.pending_new_message_recipient_guid; },
      set(value) { this.mainStore.pending_new_message_recipient_guid = value; },
    },
    internal_messaging_state: {
      get() { return this.mainStore.internal_messaging_state; },
      set(value) { this.mainStore.internal_messaging_state = value; },
    },
    contact_origin_view: {
      get() { return this.mainStore.contact_origin_view; },
      set(value) { this.mainStore.contact_origin_view = value; },
    },
    messenger_search_query_trimmed() {
      return (this.messenger_search_query || '').trim();
    },
    search_results_label() {
      const count = this.contacts_filtered.length;
      return `${count} ${count === 1 ? 'Result' : 'Results'}`;
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    alphabet_letters() {
      return ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z','#'];
    },
    contacts_sorted() {
      const self = this;
      const enterpriseUsers = this.enterprise_users_data?.enterpriseUsers || [];
      return enterpriseUsers
        .filter(user => {
          return user.messagingEnabled
              && user.personGuid
              && user.personGuid !== self.current_user_guid
              && user.personGuid.indexOf('system_') < 0;
        })
        .slice()
        .sort((a, b) => (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase()));
    },
    contacts_filtered() {
      const query = this.messenger_search_query_trimmed.toLowerCase();
      if(!query) return this.contacts_sorted;
      return this.contacts_sorted.filter(user => {
        const haystack = `${user.email || ''} ${user.friendlyName || ''} ${user.fullName || ''} ${user.userId || ''}`.toLowerCase();
        return haystack.includes(query);
      });
    },
    contacts_grouped() {
      // Bucket the filtered list into { letter, contacts } groups keyed by the first character
      // of fullName (uppercased, diacritic-folded). Anything that doesn't start with a basic
      // Latin letter after folding goes into '#'. Order: A-Z, then '#'.
      // NFD decomposes accented chars (e.g. 'É' → 'E' + combining acute); we strip the
      // combining marks (\p{M}) so "Étienne" buckets into 'E' and matches the right-rail's
      // 'E' jump target.
      const buckets = {};
      this.contacts_filtered.forEach(user => {
        const raw = ((user.fullName || '').trim().charAt(0) || '').toUpperCase();
        const folded = raw.normalize('NFD').replace(/\p{M}/gu, '');
        const letter = /^[A-Z]$/.test(folded) ? folded : '#';
        if(!buckets[letter]) buckets[letter] = [];
        buckets[letter].push(user);
      });
      const orderedLetters = Object.keys(buckets).sort((a, b) => {
        if(a === '#') return 1;
        if(b === '#') return -1;
        return a.localeCompare(b);
      });
      return orderedLetters.map(letter => ({ letter, contacts: buckets[letter] }));
    },
    color_for_guid() {
      const palette = this.$FUSfusionAvatarColorsHex || [];
      const assignments = {};
      this.contacts_sorted.forEach((user, idx) => {
        assignments[user.personGuid] = palette.length ? palette[idx % palette.length] : '#49A4E3';
      });
      return assignments;
    },
  },
  methods: {
    jumpToLetter(letter) {
      const el = document.getElementById(`fusion_contacts_section_${letter}`);
      if(el && typeof el.scrollIntoView === 'function')
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    resetMessengerSearch() {
      this.messenger_search_active = false;
      this.messenger_search_query = '';
    },
    openContact(personGuid) {
      if(!personGuid) return;
      // Opened from the Contacts list — clear any stale origin breadcrumb so Back returns to
      // this list (the default), not a previously-visited Chat Details page.
      this.contact_origin_view = null;
      this.open_contact_guid = personGuid;
      this.internal_messaging_state = 'OpenContact';
    },
  },
}
</script>
